import { WpWebsiteService } from "@/services";

// Menu API constants
const SITE_MENU_API = "shared/v1/menus/";
const SITE_INFO_API = "shared/v1/site-info";

const PRIMARY_MENU = "main-menu";
const SECONDARY_MENU = "navigation-menu";
const COMMUNITY_MENU = "community-menu";

const state = {
  allMenusData: null,
  siteInfo: null,
  loading: false,
  error: null,
  lastFetched: null,
  fetchingInProgress: false,
};

const getters = {
  allMenusData: (state) => state.allMenusData,
  menuLoading: (state) => state.loading,
  menuError: (state) => state.error,

  // Site info getter - returns the entire object
  siteInfo: (state) => state.siteInfo || {},

  // Get specific menu by slug
  getMainMenu: (state) => {
    if (!state.allMenusData?.menus) return { items: [] };
    const mainMenu = state.allMenusData.menus.find(menu => menu.menu_slug === PRIMARY_MENU);
    return mainMenu || { items: [] };
  },

  getFooterNavigationMenu: (state) => {
    if (!state.allMenusData?.menus) return { items: [] };
    const footerMenu = state.allMenusData.menus.find(menu => menu.menu_slug === SECONDARY_MENU);
    return footerMenu || { items: [] };
  },

  getFooterCommunityMenu: (state) => {
    if (!state.allMenusData?.menus) return { items: [] };
    const footerMenu = state.allMenusData.menus.find(menu => menu.menu_slug === COMMUNITY_MENU);
    return footerMenu || { items: [] };
  },

  // Legacy getters for backward compatibility
  sortedMenuItems: (state) => {
    const mainMenu = state.allMenusData?.menus?.find(menu => menu.menu_slug === PRIMARY_MENU);
    if (!mainMenu?.items) return [];
    return mainMenu.items
      .filter((item) => item.parent === "0")
      .sort((a, b) => a.menu_order - b.menu_order);
  },

  hasChildren: (state) => (itemId) => {
    const mainMenu = state.allMenusData?.menus?.find(menu => menu.menu_slug === PRIMARY_MENU);
    if (!mainMenu?.items) return false;
    return mainMenu.items.some(
      (item) => item.parent === itemId.toString()
    );
  },

  getChildren: (state) => (itemId) => {
    const mainMenu = state.allMenusData?.menus?.find(menu => menu.menu_slug === PRIMARY_MENU);
    if (!mainMenu?.items) return [];
    return mainMenu.items
      .filter((item) => item.parent === itemId.toString())
      .sort((a, b) => a.menu_order - b.menu_order);
  },
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_FETCHING_IN_PROGRESS(state, fetching) {
    state.fetchingInProgress = fetching;
  },
  SET_ALL_MENUS_DATA(state, data) {
    state.allMenusData = data;
    state.lastFetched = Date.now();
    state.error = null;
  },
  SET_SITE_INFO(state, data) {
    state.siteInfo = data;
    state.lastFetched = Date.now();
    state.error = null;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_DATA(state) {
    state.allMenusData = null;
    state.siteInfo = null;
    state.error = null;
    state.lastFetched = null;
    state.fetchingInProgress = false;
  },
};

const actions = {
  async fetchAllMenusData({ commit, state }, { forceRefresh = false } = {}) {
    // Check cache first
    if (!forceRefresh && state.allMenusData && state.lastFetched) {
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
      if (Date.now() - state.lastFetched < CACHE_DURATION) {
        return state.allMenusData;
      }
    }

    try {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      const response = await WpWebsiteService(`${SITE_MENU_API}`).get("/");

      if (!response?.data) {
        throw new Error("Invalid response from menus API");
      }

      commit("SET_ALL_MENUS_DATA", response.data);

      // Update app loading state
      document.body.classList.remove("app-loading");
      document.body.classList.add("app-loaded");

      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch menus data";
      commit("SET_ERROR", errorMessage);

      // Update app loading state even on error
      document.body.classList.remove("app-loading");
      document.body.classList.add("app-loaded");

      console.error("Menus fetch error:", errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchSiteInfo({ commit, state }, { forceRefresh = false } = {}) {
    // Check cache first
    if (!forceRefresh && state.siteInfo && state.lastFetched) {
      const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes (longer cache for site info)
      if (Date.now() - state.lastFetched < CACHE_DURATION) {
        return state.siteInfo;
      }
    }

    try {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      const response = await WpWebsiteService(`${SITE_INFO_API}`).get("/");

      if (!response?.data) {
        throw new Error("Invalid response from site-info API");
      }

      commit("SET_SITE_INFO", response.data);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch site info";
      commit("SET_ERROR", errorMessage);

      console.error("Site info fetch error:", errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  // Fetch both all menus and site info
  async fetchAllData({ dispatch, commit, state }) {
    // Prevent concurrent requests
    if (state.fetchingInProgress) {
      console.log("Menu data fetch already in progress, skipping duplicate request");
      return;
    }

    try {
      commit("SET_FETCHING_IN_PROGRESS", true);
      await Promise.all([
        dispatch("fetchAllMenusData"),
        dispatch("fetchSiteInfo")
      ]);
    } catch (error) {
      console.error("Error fetching all header data:", error);
      throw error;
    } finally {
      commit("SET_FETCHING_IN_PROGRESS", false);
    }
  },

  clearMenuData({ commit }) {
    commit("CLEAR_DATA");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
